import React from 'react';
import { alpha, Box, Stack, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { SETTINGS_SECTIONS } from './settingsSections';

/**
 * Einheitlicher Kopf einer Einstellungsseite: Icon, Name und Kurzbeschreibung kommen aus
 * `settingsSections.ts`, damit Navigation, Übersichtskachel und Seite garantiert dasselbe
 * sagen. Die Seiten selbst rendern keine eigene Überschrift mehr — die Kopfleiste der App
 * trägt „Einstellungen", dieser Kopf den Bereich.
 */
export const SettingsPageHeader: React.FC<{
    /** `key` des Bereichs aus `SETTINGS_SECTIONS`. */
    sectionKey: string;
    /** Optionale Anzeige neben dem Titel, z. B. ein Status-Chip. */
    chip?: React.ReactNode;
    /** Hauptaktion(en) der Seite, rechtsbündig. */
    actions?: React.ReactNode;
}> = ({ sectionKey, chip, actions }) => {
    const { t } = useTranslation();
    const section = SETTINGS_SECTIONS.find((s) => s.key === sectionKey);
    if (!section) return null;

    return (
        <Stack direction="row" spacing={1.75} sx={{ alignItems: 'flex-start', mb: 2.5 }}>
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2.5,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.1rem',
                    color: 'primary.main',
                    bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.1),
                }}
            >
                <FontAwesomeIcon icon={section.icon} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                    <Typography component="h2" variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {t(section.labelKey)}
                    </Typography>
                    {chip}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {t(section.descriptionKey)}
                </Typography>
            </Box>
            {actions && <Box sx={{ flexShrink: 0, pt: 0.5 }}>{actions}</Box>}
        </Stack>
    );
};

export default SettingsPageHeader;
